import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { UserRole, getUserRoleString } from '@/types/api';
import { Card } from '@/components/Card';
import { Badge } from '@/components/Badge';

// Dashboard stats interface
interface DashboardStat {
  label: string;
  value: string | number;
  change?: {
    value: string;
    type: 'increase' | 'decrease';
  };
  icon?: React.ReactNode;
  variant?: 'default' | 'success' | 'warning' | 'error' | 'info';
  href?: string; // Optional navigation link
}

// Dashboard layout props
interface DashboardLayoutProps {
  title: string;
  subtitle?: string;
  stats?: DashboardStat[];
  actions?: React.ReactNode;
  children: React.ReactNode;
}

/**
 * Shared Dashboard Layout Component
 * 
 * Provides consistent layout for all dashboard pages with
 * header, stats, and content sections.
 */
export function DashboardLayout({
  title,
  subtitle,
  stats = [],
  actions,
  children
}: DashboardLayoutProps) {
  const { user } = useAuth();
  const [showRoleInfo, setShowRoleInfo] = useState(false);

  return (
    <div className="space-y-6">
      {/* Dashboard Header */}
      <div className="bg-white shadow">
        <div className="px-4 sm:px-6 lg:px-8">
          <div className="py-6 md:flex md:items-center md:justify-between">
            <div className="flex-1 min-w-0">
              <div className="flex items-center space-x-3">
                <h1 className="text-2xl font-bold leading-7 text-gray-900 sm:text-3xl">
                  {title}
                </h1>
                <div className="relative">
                  <button
                    onClick={() => setShowRoleInfo(!showRoleInfo)}
                    className="transition-all duration-200 hover:scale-105 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 rounded"
                    title="Click to view role information"
                  >
                    <Badge variant="info" size="sm" className="cursor-pointer hover:bg-blue-200">
                      {user?.role !== undefined ? getUserRoleString(user.role) : 'Unknown'}
                    </Badge>
                  </button>

                  {/* Role Info Popup */}
                  {showRoleInfo && (
                    <>
                      {/* Backdrop */}
                      <div
                        className="fixed inset-0 z-10"
                        onClick={() => setShowRoleInfo(false)}
                      />

                      {/* Popup */}
                      <div className="absolute top-full left-0 mt-2 w-64 bg-white rounded-lg shadow-lg border border-gray-200 z-20 p-4">
                        <div className="text-sm">
                          <h3 className="font-semibold text-gray-900 mb-2">Role Information</h3>
                          <div className="space-y-2">
                            <div>
                              <span className="text-gray-600">Current Role:</span>
                              <span className="ml-2 font-medium text-gray-900">{user?.role !== undefined ? getUserRoleString(user.role) : 'Unknown'}</span>
                            </div>
                            <div>
                              <span className="text-gray-600">User:</span>
                              <span className="ml-2 font-medium text-gray-900">{user?.firstName} {user?.lastName}</span>
                            </div>
                            <div>
                              <span className="text-gray-600">Email:</span>
                              <span className="ml-2 font-medium text-gray-900">{user?.email}</span>
                            </div>
                          </div>

                          <div className="mt-3 pt-3 border-t border-gray-200">
                            <Link
                              to="/profile"
                              className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                              onClick={() => setShowRoleInfo(false)}
                            >
                              View Profile →
                            </Link>
                          </div>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>
              {subtitle && (
                <p className="mt-1 text-sm text-gray-500">
                  {subtitle}
                </p>
              )}
            </div>
            {actions && (
              <div className="mt-6 flex space-x-3 md:mt-0 md:ml-4">
                {actions}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Stats Grid */}
      {stats.length > 0 && (
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {stats.map((stat, index) => {
            const statTestId = `dashboard-stat-${stat.label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`;
            const StatCard = ({ children }: { children: React.ReactNode }) =>
              stat.href ? (
                <Link
                  to={stat.href}
                  className="bg-white overflow-hidden shadow rounded-lg hover:shadow-md transition-shadow duration-200 cursor-pointer block"
                >
                  {children}
                </Link>
              ) : (
                <div className="bg-white overflow-hidden shadow rounded-lg">
                  {children}
                </div>
              );

            return (
              <StatCard key={index}>
                <div data-testid={statTestId} className="p-5">
                  <div className="flex items-center">
                    {stat.icon && (
                      <div className="flex-shrink-0">
                        <div className={`h-8 w-8 ${stat.href ? 'text-primary-500' : 'text-gray-400'}`}>
                          {stat.icon}
                        </div>
                      </div>
                    )}
                    <div className={`${stat.icon ? 'ml-5' : ''} w-0 flex-1`}>
                      <dl>
                        <dt className="text-sm font-medium text-gray-500 truncate">
                          {stat.label}
                        </dt>
                        <dd className="flex items-baseline">
                          <div className={`text-2xl font-semibold ${stat.href ? 'text-primary-900' : 'text-gray-900'}`}>
                            {stat.value}
                          </div>
                          {stat.change && (
                            <div className={`ml-2 flex items-baseline text-sm font-semibold ${
                              stat.change.type === 'increase' ? 'text-green-600' : 'text-red-600'
                            }`}>
                              {stat.change.type === 'increase' ? (
                                <svg className="self-center flex-shrink-0 h-5 w-5 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M5.293 9.707a1 1 0 010-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 01-1.414 1.414L11 7.414V15a1 1 0 11-2 0V7.414L6.707 9.707a1 1 0 01-1.414 0z" clipRule="evenodd" />
                                </svg>
                              ) : (
                                <svg className="self-center flex-shrink-0 h-5 w-5 text-red-500" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M14.707 10.293a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 111.414-1.414L9 12.586V5a1 1 0 012 0v7.586l2.293-2.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                </svg>
                              )}
                              <span className="sr-only">
                                {stat.change.type === 'increase' ? 'Increased' : 'Decreased'} by
                              </span>
                              {stat.change.value}
                            </div>
                          )}
                        </dd>
                      </dl>
                    </div>
                  </div>
                </div>
              </StatCard>
            );
          })}
        </div>
      )}

      {/* Main Content */}
      <div className="space-y-6">
        {children}
      </div>
    </div>
  );
}

// Welcome message component
interface WelcomeCardProps {
  user: {
    firstName: string;
    role: UserRole;
  };
}

export function WelcomeCard({ user }: WelcomeCardProps) {
  const getWelcomeMessage = (role: UserRole): string => {
    switch (role) {
      case UserRole.Admin:
        return `Welcome back, ${user.firstName}! You have full access to manage the support system.`;
      case UserRole.Agent:
        return `Welcome back, ${user.firstName}! Ready to help customers today?`;
      case UserRole.EndUser:
        return `Welcome back, ${user.firstName}! How can we help you today?`;
      default:
        return `Welcome back, ${user.firstName}!`;
    }
  };

  const getRoleInfo = (role: UserRole): { color: string; description: string } => {
    switch (role) {
      case UserRole.Admin:
        return {
          color: 'text-purple-600',
          description: 'You can manage all aspects of the support system including users, settings, and reports.'
        };
      case UserRole.Agent:
        return {
          color: 'text-blue-600',
          description: 'You can view and manage tickets, communicate with customers, and access the knowledge base.'
        };
      case UserRole.EndUser:
        return {
          color: 'text-green-600',
          description: 'You can create tickets, track your requests, and access self-service resources.'
        };
      default:
        return {
          color: 'text-gray-600',
          description: 'Welcome to the support desk system.'
        };
    }
  };

  const roleInfo = getRoleInfo(user.role);

  return (
    <Card>
      <div className="p-6">
        <div className="flex items-center space-x-3">
          <div className="flex-shrink-0">
            <div className={`h-10 w-10 rounded-full ${roleInfo.color} bg-opacity-10 flex items-center justify-center`}>
              <svg className={`h-6 w-6 ${roleInfo.color}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
            </div>
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-medium text-gray-900">
              {getWelcomeMessage(user.role)}
            </h3>
            <p className="mt-1 text-sm text-gray-500">
              {roleInfo.description}
            </p>
          </div>
        </div>
      </div>
    </Card>
  );
}

// Recent activity card
interface RecentActivity {
  id: string;
  type: 'ticket_created' | 'ticket_updated' | 'ticket_assigned' | 'user_login';
  title: string;
  description: string;
  timestamp: string;
  user?: {
    name: string;
    role: UserRole;
  };
}

interface RecentActivityCardProps {
  activities: RecentActivity[];
  title?: string;
  emptyMessage?: string;
}

export function RecentActivityCard({ 
  activities, 
  title = "Recent Activity",
  emptyMessage = "No recent activity"
}: RecentActivityCardProps) {
  const getActivityIcon = (type: string) => {
    switch (type) {
      case 'ticket_created':
        return (
          <svg className="h-5 w-5 text-green-500" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
          </svg>
        );
      case 'ticket_updated':
        return (
          <svg className="h-5 w-5 text-blue-500" fill="currentColor" viewBox="0 0 20 20">
            <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
          </svg>
        );
      case 'ticket_assigned':
        return (
          <svg className="h-5 w-5 text-purple-500" fill="currentColor" viewBox="0 0 20 20">
            <path d="M13 6a3 3 0 11-6 0 3 3 0 016 0zM18 8a2 2 0 11-4 0 2 2 0 014 0zM14 15a4 4 0 00-8 0v3h8v-3z" />
          </svg>
        );
      default:
        return (
          <svg className="h-5 w-5 text-gray-500" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
          </svg>
        );
    }
  };

  return (
    <Card title={title}>
      <div className="p-6">
        {activities.length === 0 ? (
          <div className="text-center py-8">
            <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p className="mt-2 text-sm text-gray-500">{emptyMessage}</p>
          </div>
        ) : (
          <div className="flow-root">
            <ul className="-mb-8">
              {activities.map((activity, activityIdx) => (
                <li key={activity.id}>
                  <div className="relative pb-8">
                    {activityIdx !== activities.length - 1 ? (
                      <span
                        className="absolute top-4 left-4 -ml-px h-full w-0.5 bg-gray-200"
                        aria-hidden="true"
                      />
                    ) : null}
                    <div className="relative flex space-x-3">
                      <div className="h-8 w-8 rounded-full bg-gray-100 flex items-center justify-center ring-8 ring-white">
                        {getActivityIcon(activity.type)}
                      </div>
                      <div className="min-w-0 flex-1 pt-1.5 flex justify-between space-x-4">
                        <div>
                          <p className="text-sm text-gray-900">
                            {activity.title}
                          </p>
                          <p className="text-sm text-gray-500">
                            {activity.description}
                          </p>
                        </div>
                        <div className="text-right text-sm whitespace-nowrap text-gray-500">
                          <time>{activity.timestamp}</time>
                        </div>
                      </div>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </Card>
  );
}